import i18next from 'i18next';
import { toast } from 'sonner';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

/**
 * Tenant LDAP directory mutations — toast owner for API outcomes.
 * Client-side validation toasts stay on the page.
 */
export class TenantDirectoryIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    async getConfig({ silent = false } = {}) {
        try {
            const response = await this.service.getConfig();
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (!silent) toast.error(message);
            return { success: false, error: message };
        }
    }

    async listRoleMappings() {
        try {
            const response = await this.service.listRoleMappings();
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async upsertConfig(payload) {
        try {
            const response = await this.service.upsertConfig(payload);
            toast.success(
                i18next.t('definitionsNav:tenantDirectory.messages.saved', {
                    defaultValue: 'Directory settings saved.',
                }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async syncNow() {
        try {
            const response = await this.service.syncNow();
            const alreadyRunning = Boolean(response.data?.alreadyRunning);
            toast.success(
                alreadyRunning
                    ? i18next.t('definitionsNav:tenantDirectory.messages.syncAlreadyRunning', {
                        defaultValue: 'Directory sync is already running.',
                    })
                    : i18next.t('definitionsNav:tenantDirectory.messages.syncStarted', {
                        defaultValue: 'Directory sync started. The last sync time updates when it finishes.',
                    }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async listDirectoryFacets() {
        try {
            const response = await this.service.listDirectoryFacets();
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async listDirectoryUsers(params = {}) {
        try {
            const response = await this.service.listDirectoryUsers(params);
            return { success: true, data: response.data, meta: response.meta };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async importDirectoryUsers(payload) {
        try {
            const response = await this.service.importDirectoryUsers(payload);
            const created = response.data?.created || 0;
            const linked = response.data?.linked || 0;
            const converted = response.data?.converted || 0;
            const skipped = response.data?.skipped || 0;
            const errorCount = response.data?.errors?.length || 0;
            const toastFn = errorCount && !created && !linked && !converted ? toast.error : toast.success;
            toastFn(
                i18next.t('definitionsNav:tenantDirectory.messages.usersImported', {
                    created,
                    linked,
                    converted,
                    skipped,
                    errorCount,
                    defaultValue: '{{created}} imported, {{converted}} switched to LDAP, {{linked}} linked, {{skipped}} already imported, {{errorCount}} failed.',
                }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async createRoleMapping(payload) {
        try {
            const response = await this.service.createRoleMapping(payload);
            toast.success(
                i18next.t('definitionsNav:tenantDirectory.messages.mappingCreated', {
                    defaultValue: 'Mapping created.',
                }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async deleteRoleMapping(id) {
        try {
            await this.service.deleteRoleMapping(id);
            toast.success(
                i18next.t('definitionsNav:tenantDirectory.messages.mappingDeleted', {
                    defaultValue: 'Mapping deleted.',
                }),
            );
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }
}
